'use client'
import {
  type ChangeEvent,
  type DragEvent,
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import { type ImageSlice, type SupportedImageMime, processImageFile } from '../lib/image-processor'
import { type GapKind, type XPreviewProfile, webTextBelowProfile } from '../lib/x-layout-profile'
import { XPreview } from './XPreview'

declare global {
  interface Window {
    gtag?: (command: string, eventName: string, params?: Record<string, unknown>) => void
  }
}

const ACCEPTED_MIME_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/heic', 'image/heif']
const ACCEPTED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'heic', 'heif']
type GaEventName = 'file_select' | 'process_start' | 'zip_download'
type SaveAs = typeof import('file-saver')['saveAs']

const formatSize = (bytes: number): string => `${(bytes / 1024 / 1024).toFixed(2)} MB`

const isSupportedFile = (file: File): boolean => {
  const mime = file.type.toLowerCase()
  const extension = file.name.toLowerCase().split('.').pop() ?? ''

  return ACCEPTED_MIME_TYPES.includes(mime) || ACCEPTED_EXTENSIONS.includes(extension)
}

const useObjectUrlState = (): [string[], (blobs: Blob[]) => void, () => void] => {
  const [urls, setUrls] = useState<string[]>([])
  const urlsRef = useRef<string[]>([])

  const revokeAll = useCallback((targets: string[]) => {
    targets.forEach((url) => {
      URL.revokeObjectURL(url)
    })
  }, [])

  const assign = useCallback(
    (blobs: Blob[]) => {
      setUrls((previous) => {
        revokeAll(previous)
        const nextUrls = blobs.map((blob) => URL.createObjectURL(blob))
        urlsRef.current = nextUrls
        return nextUrls
      })
    },
    [revokeAll]
  )

  const clear = useCallback(() => {
    setUrls((previous) => {
      revokeAll(previous)
      urlsRef.current = []
      return []
    })
  }, [revokeAll])

  useEffect(() => {
    return () => {
      revokeAll(urlsRef.current)
    }
  }, [revokeAll])

  return [urls, assign, clear]
}

const trackEvent = (name: GaEventName, params?: Record<string, unknown>) => {
  const gtag = typeof window !== 'undefined' && 'gtag' in window ? window.gtag : undefined
  if (gtag) {
    gtag('event', name, params ?? {})
  }
}

let saveAsPromise: Promise<SaveAs> | null = null

const loadSaveAs = async (): Promise<SaveAs> => {
  const ongoing = saveAsPromise
  const nextPromise =
    ongoing ??
    import('file-saver').then((module) => {
      const direct = typeof module.saveAs === 'function' ? module.saveAs : null
      const fallback = typeof module.default === 'function' ? module.default : null
      const candidate = direct ?? fallback
      if (!candidate) {
        throw new Error('Failed to load file-saver')
      }
      return candidate
    })
  saveAsPromise = nextPromise
  return nextPromise
}

export const ImageSplitter = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [gapKind, setGapKind] = useState<GapKind>(webTextBelowProfile.defaultGapKind)
  const [viewportWidth, setViewportWidth] = useState<number>(
    webTextBelowProfile.containerWidthRule.kind === 'fixed'
      ? webTextBelowProfile.containerWidthRule.width
      : webTextBelowProfile.containerWidthRule.max
  )
  const [zipBlob, setZipBlob] = useState<Blob | null>(null)
  const [splitBlobs, setSplitBlobs] = useState<Blob[]>([])
  const [outputMime, setOutputMime] = useState<SupportedImageMime>('image/png')
  const [isProcessing, setIsProcessing] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [previews, assignPreviews, clearPreviews] = useObjectUrlState()
  const [slices, setSlices] = useState<ImageSlice[]>([])
  const fileInputId = 'image-file-input'
  const fileHelpTextId = 'image-file-help'

  const fileSummary = useMemo(
    () =>
      selectedFile
        ? `${selectedFile.name} ・ ${selectedFile.type || 'type/unknown'} ・ ${formatSize(selectedFile.size)}`
        : 'ファイルが未選択です',
    [selectedFile]
  )

  const baseFileStem = useMemo(() => {
    const segments = selectedFile ? selectedFile.name.split('.') : []
    const stem = segments.length > 1 ? segments.slice(0, -1).join('.') : segments.join('.')
    return stem.length > 0 ? stem : 'split4x'
  }, [selectedFile])

  const imageExtension = outputMime === 'image/jpeg' ? 'jpg' : 'png'

  const previewProfile = webTextBelowProfile

  const resolveContainerWidth = useCallback(
    (profile: XPreviewProfile, viewport: number) =>
      profile.containerWidthRule.kind === 'fixed'
        ? profile.containerWidthRule.width
        : Math.round(
          Math.min(
            profile.containerWidthRule.max,
            Math.max(profile.containerWidthRule.min, viewport - profile.containerWidthRule.padding * 2)
          )
        ),
    []
  )

  const lastAutoKeyRef = useRef<string | null>(null)

  const gapPx = previewProfile.gapCssPx[gapKind]

  const zipFileName = useMemo(() => {
    return `${baseFileStem}.zip`
  }, [baseFileStem])

  const individualFileName = useCallback(
    (index: number) => `${baseFileStem}_${String(index + 1)}.${imageExtension}`,
    [baseFileStem, imageExtension]
  )

  const handleFile = useCallback(
    (file: File | null) => {
      const supported = file ? isSupportedFile(file) : false
      lastAutoKeyRef.current = null
      const nextMessage = file
        ? supported
          ? null
          : '対応していない形式です。PNG / JPEG / WebP / HEIC を選んでください。'
        : '画像ファイルを選択してください。'
      setMessage(nextMessage)
      const nextFile = supported ? file : null
      setSelectedFile(nextFile)
      setZipBlob(null)
      setSplitBlobs([])
      setOutputMime('image/png')
      setSlices([])
      const resetPreviews = supported ? null : clearPreviews
      if (resetPreviews) {
        resetPreviews()
      }
      const trackingPayload =
        supported && file
          ? { file_type: file.type || 'unknown', file_size: file.size, source: 'select' }
          : null
      if (trackingPayload) {
        trackEvent('file_select', trackingPayload)
      }
    },
    [clearPreviews]
  )

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files ? event.target.files.item(0) : null
      handleFile(file)
    },
    [handleFile]
  )

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      const file = event.dataTransfer.files.item(0)
      handleFile(file)
    },
    [handleFile]
  )

  const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
  }, [])

  const handleProcess = useCallback(
    async (event?: FormEvent) => {
      event?.preventDefault()
      setIsProcessing(true)
      setMessage(null)
      clearPreviews()
      setSplitBlobs([])
      setOutputMime('image/png')
      setSlices([])

      const targetFile = selectedFile
      const containerWidth = resolveContainerWidth(previewProfile, viewportWidth)
      const processPromise = targetFile
        ? processImageFile(targetFile, gapPx, containerWidth, { outputMime: 'image/png', containerWidth })
        : Promise.reject(new Error('画像ファイルを選択してください。'))
      if (targetFile) {
        trackEvent('process_start', {
          file_type: targetFile.type || 'unknown',
          file_size: targetFile.size,
          gap: gapPx
        })
      }

      try {
        const result = await processPromise
        assignPreviews(result.blobs)
        setZipBlob(result.zipBlob)
        setSplitBlobs(result.blobs)
        setOutputMime(result.mimeType)
        setSlices(result.slices)
        setMessage('分割とプレビューが完了しました。ZIP をダウンロードできます。')
      } catch (error) {
        const description =
          error instanceof Error ? error.message : '処理に失敗しました。もう一度お試しください。'
        setMessage(description)
        setZipBlob(null)
      } finally {
        setIsProcessing(false)
      }
    },
    [assignPreviews, clearPreviews, gapPx, previewProfile, resolveContainerWidth, selectedFile, viewportWidth]
  )

  const handleDownloadSingle = useCallback(
    async (index: number) => {
      const targetBlob = splitBlobs[index]
      try {
        const saveAs = await loadSaveAs()
        const fileName = individualFileName(index)
        saveAs(targetBlob, fileName)
      } catch (error) {
        const description =
          error instanceof Error ? error.message : 'ダウンロードに失敗しました。再度お試しください。'
        setMessage(description)
      }
    },
    [individualFileName, splitBlobs]
  )

  const handleDownload = useCallback(async () => {
    const ready = zipBlob
    const saveTask = ready ? loadSaveAs() : null
    const shouldDownload = ready && saveTask
    try {
      if (shouldDownload) {
        trackEvent('zip_download', { file_name: zipFileName, blob_size: ready.size })
        const saveAs = await saveTask
        saveAs(ready, zipFileName)
      } else {
        setMessage('まず画像を分割してください。')
      }
    } catch (error) {
      const description =
        error instanceof Error ? error.message : 'ダウンロードに失敗しました。再度お試しください。'
      setMessage(description)
    }
  }, [zipBlob, zipFileName])

  const hasPreviews = previews.length === 4
  const hasSplitBlobs = splitBlobs.length === 4

  useEffect(() => {
    if (!selectedFile) {
      lastAutoKeyRef.current = null
      return
    }
    const containerWidth = resolveContainerWidth(previewProfile, viewportWidth)
    const key = [
      selectedFile.name,
      String(selectedFile.size),
      String(selectedFile.lastModified),
      previewProfile.name,
      gapKind,
      String(containerWidth)
    ].join('-')
    const isRepeated = lastAutoKeyRef.current === key
    if (isRepeated) {
      return
    }
    lastAutoKeyRef.current = key
    void handleProcess()
  }, [gapKind, selectedFile, handleProcess])

  return (
    <main className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-stone-950 px-6 py-16 text-white">
      <div className="mx-auto flex max-w-6xl flex-col gap-10">
        <header className="flex flex-col gap-4">
          <p className="inline-flex w-fit items-center gap-2 rounded-full border border-white/10 bg-white/10 px-4 py-2 text-sm font-semibold uppercase tracking-[0.2em] text-amber-200">
            Split 4 X
          </p>
          <h1 className="text-4xl font-semibold leading-tight text-white sm:text-5xl">
            X（Twitter）
            <span className="ml-3 inline-flex items-center rounded-full bg-amber-400 px-3 py-1 text-slate-950">
              四分割縦並び画像メーカー
            </span>
          </h1>
          <p className="max-w-3xl text-lg text-slate-200">
            縦長で見せたいイラストや写真を、一瞬で余白を考慮した画像に変換。HEICに対応しているので、スマホで撮影してそのまま変換できます。
          </p>
        </header>

        <form
          onSubmit={(event) => {
            event.preventDefault()
          }}
          className="grid grid-cols-1 gap-8 rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur lg:grid-cols-[1fr_1fr]"
        >
          <div className="flex flex-col gap-6">
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className="relative rounded-2xl border border-dashed border-amber-200/60 bg-white/5 p-6 shadow-inner transition hover:border-amber-300 hover:bg-white/10"
            >
              <label htmlFor={fileInputId} className="sr-only">
                画像ファイルを選択
              </label>
              <input
                id={fileInputId}
                type="file"
                accept={ACCEPTED_MIME_TYPES.join(',')}
                onChange={handleInputChange}
                aria-describedby={fileHelpTextId}
                className="absolute inset-0 cursor-pointer opacity-0"
              />
              <div className="flex flex-col items-center justify-center gap-3 py-6 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-400/20 text-amber-200">
                  ⧉
                </div>
                <div className="text-lg font-semibold text-white">ドラッグ＆ドロップ、またはクリックして選択</div>
                <p id={fileHelpTextId} className="text-sm text-slate-200">
                  対応: PNG / JPEG / WebP / HEIC
                </p>
                <p className="text-sm font-semibold text-amber-100">推奨解像度: 横 1920px × 縦 4320px</p>
                <p className="text-sm text-slate-300">{fileSummary}</p>
              </div>
            </div>

            <div className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-white/5 p-5">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex flex-1 items-center gap-2 rounded-xl border border-amber-200/40 bg-white/10 px-4 py-3 text-sm text-slate-200">
                  <span className="inline-flex h-2 w-2 rounded-full bg-amber-300" aria-hidden />
                  {isProcessing ? '画像を自動で分割・プレビュー中…' : '画像を選ぶと自動で分割＆プレビューします'}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    void handleDownload()
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-amber-200/70 bg-amber-400 px-4 py-3 text-sm font-semibold text-slate-950 transition hover:border-amber-200 hover:bg-amber-300 sm:w-auto sm:flex-none"
                >
                  分割した画像をまとめてZIPでダウンロード
                </button>
              </div>
              {message ? (
                <div className="rounded-xl border border-white/10 bg-slate-900/70 px-4 py-3 text-sm text-amber-100">
                  {message}
                </div>
              ) : null}
            </div>

            <div className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-slate-900/70 p-5">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-white">2×2 プレビュー</h2>
                <span className="rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-semibold text-slate-200">
                  各画像ダウンロード
                </span>
              </div>
              {hasPreviews ? (
                <div className="grid grid-cols-2 gap-3 rounded-xl border border-white/10 bg-black/40 p-3 shadow-inner">
                  {previews.map((url, index) => (
                    <figure
                      key={url}
                      className="group relative overflow-hidden rounded-lg border border-white/10 bg-slate-950/60"
                    >
                      <img
                        src={url}
                        alt={`分割画像 ${String(index + 1)}`}
                        className="h-40 w-full object-cover transition duration-200 group-hover:scale-[1.01]"
                      />
                      <figcaption className="absolute inset-x-0 bottom-0 flex flex-wrap items-center justify-end gap-2 bg-gradient-to-t from-black/80 via-black/50 to-transparent px-3 py-2 text-[11px] font-semibold text-white sm:text-xs">
                        <span className="min-w-0 truncate text-right">{individualFileName(index)}</span>
                        <button
                          type="button"
                          onClick={() => {
                            void handleDownloadSingle(index)
                          }}
                          disabled={!hasSplitBlobs || isProcessing}
                          className="rounded-lg border border-amber-200/60 bg-amber-400/90 px-2 py-1 text-[11px] font-bold text-slate-950 transition hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-60 sm:px-3"
                        >
                          保存
                        </button>
                      </figcaption>
                    </figure>
                  ))}
                </div>
              ) : (
                <div className="flex min-h-[220px] items-center justify-center rounded-xl border border-dashed border-white/10 bg-slate-900/40 p-6 text-center text-sm text-slate-200">
                  分割後の 4枚がここに 2×2 で表示されます。画像を選択すると自動で分割されます。
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <XPreview
              imageUrls={previews}
              slices={slices}
              profileName={previewProfile.name}
              gapKind={gapKind}
              viewportWidth={
                previewProfile.containerWidthRule.kind === 'fixed'
                  ? previewProfile.containerWidthRule.width
                  : viewportWidth
              }
              onGapKindChange={setGapKind}
              onViewportWidthChange={setViewportWidth}
            />
          </div>
        </form>

        <section className="grid grid-cols-1 gap-4 rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur sm:grid-cols-3">
          <div className="flex flex-col gap-2 rounded-2xl bg-slate-950/40 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-200">Step 1</p>
            <h2 className="text-lg font-semibold text-white">画像をアップロード</h2>
            <p className="text-sm text-slate-200">
              PNG / JPEG / WebP / HEIC に対応。縦長イラストや写真をそのままアップロードできます。
            </p>
          </div>
          <div className="flex flex-col gap-2 rounded-2xl bg-slate-950/40 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-200">Step 2</p>
            <h2 className="text-lg font-semibold text-white">Xでの見え方を確認</h2>
            <p className="text-sm text-slate-200">
              余白パターンを選び、見え方を確認してから分割します。
            </p>
          </div>
          <div className="flex flex-col gap-2 rounded-2xl bg-slate-950/40 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-200">Step 3</p>
            <h2 className="text-lg font-semibold text-white">ZIPで保存して投稿</h2>
            <p className="text-sm text-slate-200">
              プレビューで確認後、分割した画像をまとめたZIPをダウンロード。X（Twitter）の4枚投稿にそのまま使えます。
            </p>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-6 rounded-3xl border border-white/10 bg-slate-900/50 p-6 lg:grid-cols-[1.2fr_1fr]">
          <div className="flex flex-col gap-3">
            <h2 className="text-2xl font-semibold text-white">Split4X の特徴</h2>
            <ul className="grid grid-cols-1 gap-3 text-sm text-slate-200 sm:grid-cols-2">
              <li className="rounded-2xl border border-amber-200/40 bg-amber-400/5 px-4 py-3 font-semibold text-amber-100">
                余白カットで隙間を考慮したトリミング
              </li>
              <li className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                HEIC自動変換でiPhone写真もそのまま
              </li>
              <li className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                推奨横幅1920px、横幅最大4096px対応の高解像度分割、高画質出力
              </li>
              <li className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                ブラウザ内処理のみで、画像はサーバーへ送信しません
              </li>
              <li className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                ZIP一括ダウンロードで投稿準備を高速化
              </li>
              <li className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                PNG出力で画質とサイズを両立
              </li>
            </ul>
          </div>

          <div className="flex flex-col gap-3 rounded-2xl border border-amber-200/30 bg-amber-400/10 p-5">
            <h3 className="text-lg font-semibold text-amber-100">こんなシーンに</h3>
            <ul className="flex flex-col gap-2 text-sm text-slate-100 list-disc list-inside">
              <li>イラストの見開きや縦長一枚絵をXで大きく見せたいとき</li>
              <li>風景写真を4枚で連続表示させたいとき</li>
              <li>イベントの告知画像を分割したいとき</li>
              <li>Before/Afterの比較画像を縦長で載せたいとき</li>
            </ul>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-6 rounded-3xl border border-white/10 bg-white/5 p-6 lg:grid-cols-[1fr_1fr]">
          <div className="flex flex-col gap-3">
            <h2 className="text-2xl font-semibold text-white">FAQ</h2>
            <div className="rounded-2xl border border-white/10 bg-slate-950/40 p-4">
              <h3 className="text-lg font-semibold text-amber-100">対応する画像形式は？</h3>
              <p className="text-sm text-slate-200">
                PNG / JPEG / WebP / HEIC / HEIF に対応。縦長イラストや写真をそのままアップロードできます。
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-slate-950/40 p-4">
              <h3 className="text-lg font-semibold text-amber-100">画像はサーバーに送信されますか？</h3>
              <p className="text-sm text-slate-200">いいえ。ブラウザ内で処理し、ファイルはローカルでのみ扱われます。</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-slate-950/40 p-4">
              <h3 className="text-lg font-semibold text-amber-100">推奨の解像度や比率は？</h3>
              <p className="text-sm text-slate-200">
                横幅1920px以内の縦長画像を推奨します。Xでは4枚投稿時に自動で余白が入るため、余白設定を合わせて分割してください。
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-slate-950/40 p-4">
              <h3 className="text-lg font-semibold text-amber-100">料金はかかりますか？</h3>
              <p className="text-sm text-slate-200">無料で利用できます。ログインも不要です。広告の配信だけ許してください。</p>
            </div>
          </div>

          <div className="flex flex-col gap-3 rounded-2xl border border-amber-200/40 bg-amber-400/10 p-5">
            <h2 className="text-xl font-semibold text-amber-100">安全性とプライバシー</h2>
            <p className="text-sm text-slate-100">
              Split4X はブラウザ内で完結するクライアントサイドツールです。アップロードした画像はサーバーに送られず、ZIPもローカルで生成されます。
            </p>
            <p className="text-sm text-slate-100">
              分割、余白補正、ZIP化もすべて自分の端末内で行われるため、機密性の高い画像でも安心してご利用いただけます。
            </p>
            <p className="text-sm text-slate-100">
              プレビュー画面は投稿する際のイメージであり、実際のX上での表示は異なる場合があります。特にアスペクト比によってXでの表示が変わるため、投稿前にX上での見え方を必ずご確認ください。
            </p>
          </div>
        </section>
      </div>
    </main>
  )
}
